import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpHeaders,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  catchError,
  filter,
  from,
  Observable,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';

@Injectable()
export class JwtInterceptorService implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new Observable<string>();

  constructor(private authService: AuthService) {}

  accessRoutes = ['documentdetail', 'searchuser', 'mail'];

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    let currentUser = this.authService.currentUserValue;
    let currentLocation =
      currentUser &&
      currentUser.locationRoles &&
      currentUser.locationRoles.length > 0
        ? currentUser.locationRoles[currentUser.selectedLocationIndex]
        : null;
    const isAnon = request.params.get('isAnon') === 'true';
    if (currentUser && currentUser.token && !isAnon) {
      let token = currentUser.token;
      if (currentUser.provider === 'TEAM') {
        token = `TEAM ${currentUser['authPayload'].token.access_token}`;
        //token = `TEAM ${currentUser['authPayload'].token.id_token}`;
        //token = currentUser.token;
      }

      /*request = request.clone({
        headers: new HttpHeaders({
          'Authorization': `Bearer ${token}`,
          ...(new RegExp(this.accessRoutes.join("|")).test(request.url) && currentUser.access) && { 'Access-Token': currentUser.access },
          ...(currentLocation) && { 'Ebol-LocationId': `${currentLocation.LocationId}` },
          ...(currentLocation) && { 'Ebol-RoleId': `${currentLocation.RoleId}` },
          ...(currentUser.provider === 'TEAM' && currentUser['ad_username']) && {
            'AD_UserName': currentUser['ad_username']
          }
        })
      });*/
      request = this.addTokenHeader(request, token, currentUser);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Detectamos errores de autenticación
        if ([401, 403, 0].includes(error.status)) {
          // Solo renovamos si el provider no es TEAM
          if (currentUser && currentUser.provider !== 'TEAM') {
            return this.handle401Error(request, next);
          }
        }
        return throwError(() => error);
      }),
    );
  }

  /** Agrega el header de Authorization al request */
  private addTokenHeader(
    request: HttpRequest<any>,
    token: string,
    user: any
  ): HttpRequest<any> {
    let currentLocation =
      user && user.locationRoles && user.locationRoles.length > 0
        ? user.locationRoles[user.selectedLocationIndex]
        : null;
    let headers: Record<string, string> = { Authorization: `Bearer ${token}` };

    // Agregar X-Token-Issuer solo si access es 'team' y la URL apunta a la API
    // (apiUrl puede ser vacío, en ese caso usar window.location.origin)
    // Nota: window solo está disponible en runtime/browser
    try {
      // eslint-disable-next-line no-undef
      const apiUrl =
        environment.apiUrl && environment.apiUrl.length > 0
          ? environment.apiUrl
          : window.location.origin;
      
      if(user?.authPayload?.accessToken) {
        headers['Access-Token'] = user.authPayload.accessToken;
      }
      if (user.provider === 'TEAM' && request.url.indexOf('/api/') >= 0) {
        headers['X-Token-Issuer'] = 'TEAM';
        if (user.ad_username) {
          headers['AD_UserName'] = user.ad_username;
        }
      }
    } catch (e) {
      // window no está definido en server-side rendering, ignorar
    }

    return request.clone({ setHeaders: headers });

    //return request.clone({ setHeaders: headers });
    return request.clone({
      headers: new HttpHeaders({
        Authorization: `Bearer ${token}`,
        ...(new RegExp(this.accessRoutes.join('|')).test(request.url) &&
          user.access && { 'Access-Token': user.access }),
        ...(currentLocation && {
          'Ebol-LocationId': `${currentLocation.LocationId}`,
        }),
        ...(currentLocation && { 'Ebol-RoleId': `${currentLocation.RoleId}` }),
        ...(user.provider === 'TEAM' &&
          user['ad_username'] && {
            AD_UserName: user['ad_username'],
          }),
      }),
    });
  }

  /** Maneja el error 401 con reintento tras refresh */
  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;

      // Creamos un observable compartido para evitar múltiples refresh simultáneos
      const refresh$ = from(this.authService.verifyAndRenewToken()).pipe(
        switchMap((newToken: string | null) => {
          this.isRefreshing = false;

          if (newToken) {
            const newUser = this.authService.currentUserValue;
            const newRequest = this.addTokenHeader(request, newToken, newUser);
            return next.handle(newRequest);
          } else {
            console.error('No token recibido tras el refresh.');
            this.authService.loginRedirectAd();
            return throwError(() => new Error('No token after refresh'));
          }
        }),
        catchError((refreshError) => {
          console.error('Error al renovar token:', refreshError);
          this.isRefreshing = false;
          this.authService.loginRedirectAd();
          return throwError(() => refreshError);
        }),
      );

      return refresh$;
    } else {
      // Si ya hay un refresh en curso, esperamos a que termine
      return this.refreshTokenSubject.pipe(
        filter((token) => !!token),
        take(1),
        switchMap((token) => {
          const newUser = this.authService.currentUserValue;
          const newRequest = this.addTokenHeader(request, token, newUser);
          return next.handle(newRequest);
        }),
      );
    }
  }
}
